'use client'

import React, { memo, useState } from 'react'

import { NavigateNext, Public, SupervisorAccount, SupportAgent, Work } from '@mui/icons-material'
import {
  Avatar,
  Box,
  Card,
  Chip,
  Container,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'

interface RegionalOperationsProps {
  mounted: boolean
}

const _regionalOperations = [
  {
    region: 'Addis Ababa',
    code: 'AA',
    centers: 124,
    staff: 1847,
    dailyCapacity: 15000,
    utilization: 87,
    performance: 94,
    status: 'operational',
  },
  {
    region: 'Oromia',
    code: 'OR',
    centers: 256,
    staff: 3421,
    dailyCapacity: 28000,
    utilization: 73,
    performance: 89,
    status: 'operational',
  },
  {
    region: 'Amhara',
    code: 'AM',
    centers: 198,
    staff: 2634,
    dailyCapacity: 22000,
    utilization: 69,
    performance: 87,
    status: 'operational',
  },
  {
    region: 'SNNPR',
    code: 'SN',
    centers: 167,
    staff: 2156,
    dailyCapacity: 18000,
    utilization: 65,
    performance: 85,
    status: 'operational',
  },
  {
    region: 'Tigray',
    code: 'TG',
    centers: 89,
    staff: 1234,
    dailyCapacity: 10000,
    utilization: 71,
    performance: 88,
    status: 'maintenance',
  },
  {
    region: 'Somali',
    code: 'SM',
    centers: 76,
    staff: 987,
    dailyCapacity: 8000,
    utilization: 62,
    performance: 83,
    status: 'operational',
  },
]

const RegionalOperations = memo<RegionalOperationsProps>(({ mounted: _mounted }) => {
  const theme = useTheme()
  const [selectedTab, setSelectedTab] = useState(0)

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue)
  }

  // const _handleEventExpand = (_event: React.MouseEvent, _index: number) => {
  //   setSelectedTab(_index)
  // }

  const getStatusColor = (status: string) => {
    return status === 'operational' ? 'success' : 'warning'
  }

  const getPerformanceColor = (performance: number) => {
    if (performance >= 90) return theme.palette.success.main
    if (performance >= 80) return theme.palette.info.main
    if (performance >= 70) return theme.palette.warning.main
    return theme.palette.error.main
  }

  return (
    <Container maxWidth="xl" sx={{ py: 8 }}>
      <Box textAlign="center" mb={6}>
        <Chip
          icon={<Public />}
          label="REGIONAL OPERATIONS"
          color="primary"
          variant="outlined"
          sx={{ mb: 3, fontWeight: 600 }}
        />
        <Typography variant="h3" fontWeight={800} gutterBottom>
          Nationwide Coverage
        </Typography>
        <Typography variant="h6" color="text.secondary" maxWidth={800} mx="auto">
          Comprehensive regional deployment with localized support across all Ethiopian states
        </Typography>
      </Box>

      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        centered
        sx={{
          mb: 4,
          '& .MuiTab-root': {
            fontWeight: 600,
            fontSize: '0.95rem',
          },
        }}
      >
        <Tab label="Overview" />
        <Tab label="Performance" />
        <Tab label="Capacity" />
        <Tab label="Staff Distribution" />
      </Tabs>

      <Grid container spacing={3}>
        {_regionalOperations.map((region, _index) => (
          <Grid key={region.code} size={{ xs: 12, sm: 6, lg: 4 }}>
            <Card
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[4],
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                },
              }}
            >
              <Stack spacing={3}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: theme.palette.primary.main,
                        fontWeight: 700,
                      }}
                    >
                      {region.code}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        {region.region}
                      </Typography>
                      <Chip
                        label={region.status}
                        size="small"
                        color={getStatusColor(region.status)}
                        sx={{ fontWeight: 600, height: 20 }}
                      />
                    </Box>
                  </Stack>
                  <IconButton size="small">
                    <NavigateNext />
                  </IconButton>
                </Stack>

                {/* Tab Content */}
                {selectedTab === 0 && (
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Box>
                        <Typography variant="h5" fontWeight={700}>
                          {region.centers}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Service Centers
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size={6}>
                      <Box>
                        <Typography variant="h5" fontWeight={700}>
                          {region.staff.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Active Staff
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                )}

                {selectedTab === 1 && (
                  <Box>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={1}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Performance Score
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {region.performance}%
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={region.performance}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          bgcolor: getPerformanceColor(region.performance),
                        },
                      }}
                    />
                  </Box>
                )}

                {selectedTab === 2 && (
                  <Box>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={2}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Daily Capacity
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {region.dailyCapacity.toLocaleString()}
                      </Typography>
                    </Stack>
                    <Box>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={1}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Utilization Rate
                        </Typography>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {region.utilization}%
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={region.utilization}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: alpha(theme.palette.warning.main, 0.1),
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            bgcolor: theme.palette.warning.main,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                )}

                {selectedTab === 3 && (
                  <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Work fontSize="small" />
                        <Typography variant="body2">Field Officers</Typography>
                      </Stack>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {Math.floor(region.staff * 0.6).toLocaleString()}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <SupervisorAccount fontSize="small" />
                        <Typography variant="body2">Supervisors</Typography>
                      </Stack>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {Math.floor(region.staff * 0.3).toLocaleString()}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <SupportAgent fontSize="small" />
                        <Typography variant="body2">Support Staff</Typography>
                      </Stack>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {Math.floor(region.staff * 0.1).toLocaleString()}
                      </Typography>
                    </Stack>
                  </Stack>
                )}
              </Stack>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  )
})

RegionalOperations.displayName = 'RegionalOperations'

export default RegionalOperations
